import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, currencySymbol, fmtDate } from "@/lib/format";
import { Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const Customers = () => {
  const [rows, setRows] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers");
      setRows(r.data.customers || []);
      setSyncedAt(r.data.synced_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => !q || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
            From Zoho Books · Sales Orders + Invoices
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">
            Customers & Contracts
          </h1>
          <div className="text-sm font-mono text-[#666] mt-2">
            {syncedAt ? `Last synced: ${fmtDate(syncedAt)}` : "No data yet — go to Dashboard and Sync"}
          </div>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <input
              data-testid="customer-search"
              placeholder="Search customers..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border border-[#E5E5E5] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] outline-none w-64"
            />
          </div>
          <button
            onClick={load}
            data-testid="refresh-customers"
            className="border border-[#0A0A0A] px-3 py-2 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="customers-table">
        <table className="w-full border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#0A0A0A]">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Customer</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">CCY</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Contract Value</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Billed</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Balance to Bill</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Outstanding</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">SOs · Invs</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">USD Equiv</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-sm text-[#666] py-12">
                  {loading ? "Loading..." : "No customer data."}
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.customer_id}
                  data-testid={`customer-row-${r.customer_id}`}
                  className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9] transition-colors"
                >
                  <td className="py-3 px-4 text-sm font-medium text-[#0A0A0A]">{r.customer_name || "—"}</td>
                  <td className="py-3 px-4 text-xs font-mono text-[#666]">{r.currency}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(r.contract_value, r.currency)}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(r.billed, r.currency)}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right font-semibold">
                    {fmtMoney(r.balance_to_bill, r.currency)}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-right text-[#E53E3E]">
                    {r.outstanding > 0 ? fmtMoney(r.outstanding, r.currency) : "—"}
                  </td>
                  <td className="py-3 px-4 text-xs font-mono text-right text-[#666]">
                    {r.sales_order_count} · {r.invoice_count}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-right text-[#0A0A0A] font-medium">
                    {fmtMoney(r.contract_value_usd, "USD")}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#0A0A0A] bg-[#F5F5F5]">
                <td className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#0A0A0A]" colSpan={2}>Totals (USD)</td>
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold">{fmtMoney(filtered.reduce((s, r) => s + r.contract_value_usd, 0), "USD")}</td>
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold">{fmtMoney(filtered.reduce((s, r) => s + r.billed_usd, 0), "USD")}</td>
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold">{fmtMoney(filtered.reduce((s, r) => s + r.balance_to_bill_usd, 0), "USD")}</td>
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold text-[#E53E3E]">{fmtMoney(filtered.reduce((s, r) => s + r.outstanding_usd, 0), "USD")}</td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default Customers;
